export type VoicePromptSource = {
  script?: string | null;
  voice_guide?: string | null;
  voice_script?: string | null;
};

export type VoicePromptFields = {
  script: string;
  voice_guide: string;
  voice_script: string;
};

const DIRECTOR_NOTES = "DIRECTOR_NOTES";
const NOTE_MARKER = /^\s*DIRECTOR_NOTES:?\s*$/i;
const GUIDE_KEY = /^\s*[A-Za-z][A-Za-z _-]{0,30}:\s*.*$/;
export const DEFAULT_VOICE_GUIDE =
  "Tone: Energetic, enthusiastic, and highly professional\n" +
  "Pacing: Fast-paced, brisk, and concise. No long pauses between sentences.";

/** Split both legacy layouts: notes before spoken text and notes after it. */
export const parseLegacyVoicePrompt = (value: string | null | undefined): Omit<VoicePromptFields, "script"> => {
  const text = (value ?? "").replace(/\r\n?/g, "\n");
  const lines = text.split("\n");
  if (!lines.some((line) => NOTE_MARKER.test(line))) {
    return { voice_guide: "", voice_script: text.trim() };
  }

  const guides: string[] = [];
  const spoken: string[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!NOTE_MARKER.test(line)) {
      spoken.push(line);
      index += 1;
      continue;
    }

    index += 1;
    const guideLines: string[] = [];
    // New combined prompts use a blank line to separate free-form guidance
    // from speech. Legacy prompts without that boundary still use Key: lines.
    if (index < lines.length && lines[index].trim() && !GUIDE_KEY.test(lines[index])) {
      let end = index;
      while (end < lines.length && lines[end].trim() && !NOTE_MARKER.test(lines[end])) end += 1;
      const hasSpokenBefore = spoken.some((value) => value.trim());
      const hasSpokenAfter = end < lines.length && !lines[end].trim() && lines.slice(end + 1).some((value) => value.trim());
      const guideOnly = !hasSpokenBefore && !lines.slice(end).some((value) => value.trim());
      if (hasSpokenBefore || hasSpokenAfter || guideOnly) {
        guides.push(lines.slice(index, end).join("\n").trim());
        index = end < lines.length && !lines[end].trim() ? end + 1 : end;
        continue;
      }
    }

    while (index < lines.length) {
      const candidate = lines[index];
      if (!candidate.trim()) {
        index += 1;
        break;
      }
      if (GUIDE_KEY.test(candidate) || (guideLines.length > 0 && /^\s/.test(candidate))) {
        guideLines.push(candidate.replace(/\s+$/, ""));
        index += 1;
        continue;
      }
      break;
    }

    const guide = guideLines.join("\n").trim();
    if (guide) guides.push(guide);
  }

  return {
    voice_guide: guides.join("\n\n").trim(),
    voice_script: spoken.join("\n").replace(/\n{3,}/g, "\n\n").trim(),
  };
};

/** Keep the existing marker in the final prompt sent to Gemini TTS. */
export const composeVoicePrompt = (voiceGuide: string | null | undefined, voiceScript: string | null | undefined): string => {
  const guide = (voiceGuide ?? "").trim();
  const spoken = (voiceScript ?? "").trim();
  if (!guide) return spoken;
  if (!spoken) return `${DIRECTOR_NOTES}\n${guide}`;
  return `${DIRECTOR_NOTES}\n${guide}\n\n${spoken}`;
};

/** Add the new fields for display without changing the old script string. */
export const hydrateVoicePrompt = <T extends VoicePromptSource>(item: T): T & VoicePromptFields => {
  const legacy = parseLegacyVoicePrompt(item.script);
  return {
    ...item,
    script: item.script ?? "",
    voice_guide: item.voice_guide ?? legacy.voice_guide,
    voice_script: item.voice_script ?? legacy.voice_script,
  };
};

/** Direct-script drafts used the default direction when older input had no guide. */
export const hydrateDirectInputVoicePrompt = <T extends VoicePromptSource>(item: T): T & VoicePromptFields => {
  const voice = hydrateVoicePrompt(item);
  return {
    ...voice,
    voice_guide: item.voice_guide == null && !voice.voice_guide ? DEFAULT_VOICE_GUIDE : voice.voice_guide,
  };
};

/** Synchronize the legacy mirror only after one of the separate values changes. */
export const synchronizeVoicePrompt = <T extends VoicePromptSource>(item: T): T & VoicePromptFields => {
  const rawScript = item.script ?? "";
  const legacy = parseLegacyVoicePrompt(rawScript);
  const voiceGuide = item.voice_guide ?? legacy.voice_guide;
  const voiceScript = item.voice_script ?? legacy.voice_script;
  const script =
    rawScript && voiceGuide === legacy.voice_guide && voiceScript === legacy.voice_script
      ? rawScript
      : composeVoicePrompt(voiceGuide, voiceScript);

  return { ...item, script, voice_guide: voiceGuide, voice_script: voiceScript };
};
